/**
 * AppShell — page frame + the single liquid-glass element in the app: a floating rounded-full
 * tab bar (§4/§5). This is the manager surface, so the tabs are its own (Лента / KPI),
 * not the waiter app's (Зал / Листы / Обучение / Смена).
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { ConnectionDot } from "@/components/ConnectionDot";
import type { ConnectionState } from "@/hooks/useFeedChannel";

export type TabKey = "feed" | "tables" | "shift" | "kpi";

interface AppShellProps {
  active: TabKey;
  onTab: (t: TabKey) => void;
  connection: ConnectionState;
  dark: boolean;
  onToggleTheme: () => void;
  children: ReactNode;
}

const tabs: { key: TabKey; label: string }[] = [
  { key: "feed", label: "Лента" },
  { key: "tables", label: "Столы" },
  { key: "shift", label: "Смена" },
  { key: "kpi", label: "KPI" },
];

export function AppShell({
  active,
  onTab,
  connection,
  dark,
  onToggleTheme,
  children,
}: AppShellProps) {
  return (
    <div className="min-h-full bg-canvas text-ink">
      {/* Brand bar */}
      <div className="mx-auto flex max-w-3xl items-center justify-between px-4 pt-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold tracking-tight">
            Orees Vision
          </span>
          <span className="eyebrow">Управляющий</span>
        </div>
        <div className="flex items-center gap-3">
          <ConnectionDot state={connection} />
          <button
            type="button"
            onClick={onToggleTheme}
            className="focus-ring rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-ink"
            aria-label="Переключить тему"
          >
            {dark ? "Светлая" : "Тёмная"}
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="mx-auto max-w-3xl px-4 pb-28 pt-2">{children}</main>

      {/* Floating liquid-glass tab bar */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex justify-center pb-[max(16px,env(safe-area-inset-bottom))]">
        <ul className="glass grid grid-cols-4 gap-1 rounded-full p-1" role="tablist">
          {tabs.map((t) => {
            const isActive = t.key === active;
            return (
              <li key={t.key}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onTab(t.key)}
                  className={cn(
                    // 4 таба: min-w-20/px-4, иначе бар шире экрана телефона.
                    "focus-ring relative flex min-h-12 min-w-20 items-center justify-center rounded-full px-4 text-sm font-semibold transition-colors",
                    isActive ? "text-ink" : "text-muted",
                  )}
                >
                  {isActive && (
                    <span className="absolute inset-0 rounded-full bg-surface-2" />
                  )}
                  <span className="relative">{t.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
